// =============================================================================
// CAREER DNA EXCAVATION — the keystone corpus subsystem
//
// A chronological, voice-led "autopsy" of the user's career. Goes back to the
// origin, hunts the "normal to me" gold he'd never volunteer (curse of
// expertise), traces the thread era to era, and synthesises the through-line
// that is unmistakably him. The captured material becomes the positioning
// spine the CV + cover letters inherit.
//
// Why purpose-built tables (not the existing `interview_sessions`): that table
// is mock-interview-shaped (starScores, jobId, feedback). The excavation needs
// typed, queryable FACETS the synthesis engine compares ACROSS — so the corpus
// becomes true, not just summarised. `interview_sessions` stays for Prep.
//
// See ~/.claude/plans/foamy-brewing-river.md and docs/career-dna.md.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, real, text, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, substrate, timestamps } from './_shared';

/** One ordered turn in the conversation. Stored append-only in the jsonb
 *  `transcript`. Engines read the recent slice for context. */
export interface DnaTurn {
  /** Who spoke: the app (a question) or the user (an answer). */
  role: 'assistant' | 'user';
  text: string;
  turnIndex: number;
  /** ISO timestamp. */
  at: string;
  /** R2 key for the raw answer audio (user turns captured via Scribe). */
  audioR2Key?: string;
}

/** A connective claim between two eras — the through-line made concrete. */
export interface DnaThread {
  from: string;
  to: string;
  thread: string;
  roleIds?: string[];
}

// ── The excavation session: one chronological walk through a life/career ──
export const dnaSessions = pgTable(
  'dna_sessions',
  {
    id: id(),
    userId: text('user_id').notNull(),
    // 'origins' → 'timeline' → 'synthesis' → 'complete'. The interview-turn
    // engine advances this; the front-end renders phase-appropriate UI.
    phase: varchar('phase', { length: 16 }).notNull().default('origins'),
    // The era being excavated now: a roles.id, or null while in origins.
    currentRoleId: uuid('current_role_id'),
    // Ordered transcript of DnaTurn. Append-only. Synthesis reads it whole.
    transcript: jsonb('transcript').$type<DnaTurn[]>().default(sql`'[]'::jsonb`),
    // Voices chosen for THIS session (denormalised from voice_preferences so a
    // session stays reproducible even if prefs change later).
    interviewVoiceId: varchar('interview_voice_id', { length: 64 }), // female-AU
    statesideVoiceId: varchar('stateside_voice_id', { length: 64 }), // male-US
    ...timestamps,
  },
  (t) => ({ userIdx: index('dna_sessions_user_idx').on(t.userId) }),
);

// ── The keystone: typed facets mined from stories ──
export const dnaFacets = pgTable(
  'dna_facets',
  {
    id: id(),
    userId: text('user_id').notNull(),
    sessionId: uuid('session_id').notNull(),
    // accomplishment | knowledge | lesson | thread | driver
    facetType: varchar('facet_type', { length: 16 }).notNull(),
    // The era this came from. roleId for career eras; originLabel for
    // pre-career ('childhood' / 'first job' / 'university'). Both nullable — a
    // driver may span everything.
    roleId: uuid('role_id'),
    originLabel: varchar('origin_label', { length: 80 }),
    // Which conversational turn produced it (provenance into the transcript).
    turnIndex: integer('turn_index'),
    // The facet itself: a tight sentence/phrase. For 'thread', the connective
    // claim. For 'driver', what pushes him. Synthesis compares these.
    content: text('content').notNull(),
    // The "normal to me" gold flag — curse-of-expertise capture. 1 when the
    // distillation engine judged this is something he'd never volunteer.
    isNormalToMe: integer('is_normal_to_me').default(0).notNull(),
    // When facetType='accomplishment', the achievements.id it was promoted into.
    linkedAchievementId: uuid('linked_achievement_id'),
    // Capture provenance (mirrors InputCapture.source): voice | text | dialogue.
    source: varchar('source', { length: 16 }).notNull().default('voice'),
    voiceNoteR2Key: varchar('voice_note_r2_key', { length: 256 }),
    confidence: real('confidence'),
    // EMBEDDING SEAM — deferred, never populated in v1 (no pgvector). Mirrors
    // achievements.embedding. v1.5 retrieval optimisation when facets grow.
    embedding: jsonb('embedding'),
    // Flexible per-facet metadata (additive, W1-9 corpus-cleanup verbs). Mirrors
    // achievements.metadata's shape/intent: today it carries ONLY the VERIFY
    // stamp — { verified: { at: <iso>, terminal: true } } — a user's explicit
    // "this is correct" that permanently excludes the facet from similar-pairs /
    // conflicts / temporal-reconcile PROPOSALS (never resurfaced, the corrections
    // law). Room to grow without a migration, same discipline as achievements.
    metadata: jsonb('metadata'),
    // AXIS-P2 (corpus-proving-ground.md §1.5/§6: "858 dna_facets vs 270
    // achievements (≈3:1) — the unpromoted-facet layer is the larger text pool;
    // ... the near-miss proof lived in a FACET transcript — weight accordingly").
    // Same AxisAffinity[] shape as achievements.axisAffinities — the tagger walks
    // BOTH tables (a `driver`/`thread` facet not yet promoted to an achievement
    // still carries real axis signal, e.g. the exec-communication near-miss the
    // corpus proving-ground doc cites verbatim). Nullable: absent = untagged.
    axisAffinities: jsonb('axis_affinities'),
    ...substrate,
    ...timestamps,
  },
  (t) => ({
    userIdx: index('dna_facets_user_idx').on(t.userId),
    sessionIdx: index('dna_facets_session_idx').on(t.sessionId),
    typeIdx: index('dna_facets_type_idx').on(t.facetType),
    roleIdx: index('dna_facets_role_idx').on(t.roleId),
  }),
);

// ── The synthesised DNA: the positioning spine. Current row per user. ──
export const dnaProfiles = pgTable(
  'dna_profiles',
  {
    id: id(),
    userId: text('user_id').notNull(),
    sessionId: uuid('session_id').notNull(),
    // The 4 sections of career-dna.md, queryable.
    origins: text('origins'),
    drivers: text('drivers'),
    // The through-line: the recurring pattern unmistakably him.
    throughLine: text('through_line'),
    // Structured era→era threads for the timeline view's connective tissue.
    threads: jsonb('threads').$type<DnaThread[]>().default(sql`'[]'::jsonb`),
    // ≤2 sentences; the spine the CV/cover-letter engines consume.
    positioningSpine: text('positioning_spine'),
    modelId: varchar('model_id', { length: 64 }),
    // Bumped each re-synthesis so the UI / doc render knows the version.
    version: integer('version').default(1).notNull(),
    ...timestamps,
  },
  (t) => ({ userIdx: index('dna_profiles_user_idx').on(t.userId) }),
);

// ── Voice-ID persistence: the auditioned + chosen ElevenLabs voices. ──
export const voicePreferences = pgTable(
  'voice_preferences',
  {
    id: id(),
    userId: text('user_id').notNull(),
    // 'interview' (female-AU) | 'stateside' (male-US)
    slot: varchar('slot', { length: 16 }).notNull(),
    voiceId: varchar('voice_id', { length: 64 }).notNull(),
    voiceName: varchar('voice_name', { length: 120 }),
    previewUrl: text('preview_url'),
    // Account-drift guard (v2, see proposal 2.6). A persisted "name plus account
    // hash" stamp for the voice at select-time. On use, if it does not match the
    // live ElevenLabs account, the route falls back to a known-good default and
    // surfaces a one-line notice instead of throwing a 422/404 then a 500.
    // Nullable: pre-existing rows have none and are simply re-validated on use.
    voiceFingerprint: text('voice_fingerprint'),
    ...timestamps,
  },
  (t) => ({ userSlotIdx: index('voice_preferences_user_slot_idx').on(t.userId, t.slot) }),
);
